"use strict";
/**
 * Which Opportunity fields can start a flow, and which can be used in a flow's
 * Filters (plan sections 2.5.1 and A6). Pure, so the flow model (at switch-on),
 * the settings route that lists the fields (`canStart`, `canFilter`) and the
 * browser all use the same rule.
 *
 * A field STARTS a flow when the Opportunity is saved with a different value of
 * it. That is only true of a field Salesforce stores and changes when someone
 * edits the Opportunity. A formula or a roll-up is computed, so it changes when
 * something else changes and the Opportunity's own stamp does not move: Encharge
 * would see the new value on some later save and start the flow late, or never.
 * A field of a related record (the owner's name, the Account's industry) changes
 * the same way. Audit and system fields change whatever the person did. All of
 * those can still be used in Filters, where the flow only READS them at the
 * moment a change starts it.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.OPPORTUNITY_FILTER_MAX_RULES = exports.opportunityFieldCanFilter = exports.opportunityFieldCanStart = exports.whyOpportunityFieldCannotStart = exports.OPPORTUNITY_NEVER_FILTER_FIELDS = exports.OPPORTUNITY_NEVER_START_FIELDS = exports.OPPORTUNITY_FILTER_FIELD_NAME = exports.OPPORTUNITY_START_FIELD_NAME = void 0;
/** A field name as a flow can start on it: letters, digits and underscores, no dots (a related field has a dot). */
exports.OPPORTUNITY_START_FIELD_NAME = /^[A-Za-z][A-Za-z0-9_]{0,79}$/;
/**
 * A field a Filter can read: an Opportunity field, the owner's name, or a field
 * of the Account. Nothing deeper (Account.Owner.Name): the sync reads the
 * Account's own fields only.
 */
exports.OPPORTUNITY_FILTER_FIELD_NAME = /^(?:[A-Za-z][A-Za-z0-9_]{0,79}|Owner\.Name|Account\.[A-Za-z][A-Za-z0-9_]{0,79})$/;
/**
 * Fields that never start a flow, in lower case: the audit and system fields
 * (they change without the Opportunity's own data changing), the stage name
 * (the Stage start is for that) and IsDeleted (a deletion is applied so
 * segments can leave the Opportunity out, never to start a flow: A6).
 */
exports.OPPORTUNITY_NEVER_START_FIELDS = [
    "id",
    "systemmodstamp",
    "lastmodifieddate",
    "lastmodifiedbyid",
    "lastactivitydate",
    "lastvieweddate",
    "lastreferenceddate",
    "isdeleted",
    "stagename",
];
/**
 * Fields that are not offered in Filters: IsDeleted (A6: segments and Check
 * Condition can use it, a trigger cannot).
 */
exports.OPPORTUNITY_NEVER_FILTER_FIELDS = ["isdeleted"];
/** Types the rule engine cannot read as one value. */
const UNREADABLE_TYPES = new Set([
    "address",
    "location",
    "base64",
    "complexvalue",
]);
/**
 * Why a field cannot start a flow, in words for the person, or undefined when it
 * can. Judged by name and, when the describe is known, by its facts. With no
 * facts only the name is judged: a name that is not in the describe is not
 * refused here (the describe may be older than the field).
 */
const whyOpportunityFieldCannotStart = (name, facts) => {
    if (typeof name !== "string" || name.trim() === "") {
        return "Choose the Opportunity field that starts the flow.";
    }
    if (name.includes(".")) {
        return `${name} is a field of a related record. It changes without the Opportunity being saved, so it cannot start a flow. You can use it in Filters.`;
    }
    if (!exports.OPPORTUNITY_START_FIELD_NAME.test(name)) {
        return `${name} is not the name of an Opportunity field.`;
    }
    const lower = name.toLowerCase();
    if (lower === "stagename") {
        return "Choose Stage as what starts the flow to start it when the Opportunity changes stage.";
    }
    if (lower === "isdeleted") {
        return "A deleted Opportunity is kept in Encharge and cannot start a flow.";
    }
    if (exports.OPPORTUNITY_NEVER_START_FIELDS.includes(lower)) {
        return `${name} is a system field. It changes whatever the person edited, so it cannot start a flow.`;
    }
    if ((facts === null || facts === void 0 ? void 0 : facts.calculated) === true) {
        return `${name} is a formula or roll-up field. It changes without the Opportunity being saved, so it cannot start a flow. You can use it in Filters.`;
    }
    if ((facts === null || facts === void 0 ? void 0 : facts.type) && UNREADABLE_TYPES.has(String(facts.type).toLowerCase())) {
        return `${name} holds a kind of value that a flow cannot compare, so it cannot start a flow.`;
    }
    return undefined;
};
exports.whyOpportunityFieldCannotStart = whyOpportunityFieldCannotStart;
/** Whether a field can start a flow (see whyOpportunityFieldCannotStart). */
const opportunityFieldCanStart = (name, facts) => (0, exports.whyOpportunityFieldCannotStart)(name, facts) === undefined;
exports.opportunityFieldCanStart = opportunityFieldCanStart;
/** Whether a field can be used in a flow's Filters. */
const opportunityFieldCanFilter = (name, facts) => {
    if (typeof name !== "string" || !exports.OPPORTUNITY_FILTER_FIELD_NAME.test(name)) {
        return false;
    }
    if (exports.OPPORTUNITY_NEVER_FILTER_FIELDS.includes(name.toLowerCase())) {
        return false;
    }
    return !((facts === null || facts === void 0 ? void 0 : facts.type) && UNREADABLE_TYPES.has(String(facts.type).toLowerCase()));
};
exports.opportunityFieldCanFilter = opportunityFieldCanFilter;
/** The most rules a flow's Filters may hold, all groups and Keep out together. */
exports.OPPORTUNITY_FILTER_MAX_RULES = 20;
//# sourceMappingURL=opportunityTriggerFields.js.map